/**
 * EtfSectorOverlay — side-by-side sector exposure across the pinned tickers.
 * Renders only when at least one pinned ticker is an ETF.
 */
import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { getEtfInfo } from "@/lib/api";

const SECTOR_ORDER = [
  "technology",
  "healthcare",
  "financial_services",
  "communication_services",
  "consumer_cyclical",
  "consumer_defensive",
  "industrials",
  "energy",
  "utilities",
  "realestate",
  "basic_materials",
];

const SECTOR_LABELS = {
  technology: "Tech",
  healthcare: "Health",
  financial_services: "Financials",
  communication_services: "Comms",
  consumer_cyclical: "Cons Cyc",
  consumer_defensive: "Cons Def",
  industrials: "Industrials",
  energy: "Energy",
  utilities: "Utilities",
  realestate: "Real Estate",
  basic_materials: "Materials",
};

const SECTOR_COLOR = {
  technology: "#d7b5c9",
  healthcare: "#88a17e",
  financial_services: "#bfaf9d",
  communication_services: "#9e8193",
  consumer_cyclical: "#a3b594",
  consumer_defensive: "#c79ab5",
  industrials: "#7d8e6e",
  energy: "#a89687",
  utilities: "#dbd6d2",
  realestate: "#8d6f80",
  basic_materials: "#bea089",
};

export default function EtfSectorOverlay({ symbols }) {
  const [data, setData] = useState({}); // sym -> etf payload
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all(
      (symbols || []).map((s) =>
        getEtfInfo(s)
          .then((r) => [s, r])
          .catch(() => [s, null])
      )
    ).then((pairs) => {
      if (!alive) return;
      setData(Object.fromEntries(pairs));
      setLoading(false);
    });
    return () => {
      alive = false;
    };
  }, [symbols?.join(",")]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <div className="border border-zinc-800 bg-[#121214] p-4 flex items-center gap-2 text-xs text-zinc-500 font-mono">
        <Loader2 size={12} className="animate-spin" /> Loading sector data…
      </div>
    );
  }

  const etfs = Object.entries(data).filter(([, v]) => v && v.is_etf);
  if (!etfs.length) return null;

  return (
    <div
      data-testid="etf-sector-overlay"
      className="border border-zinc-800 bg-[#121214] p-5 sm:p-6 mb-6"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="text-label">Sector exposure · ETFs</div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
          {etfs.length} ETF{etfs.length === 1 ? "" : "s"} compared
        </div>
      </div>

      <div className="flex flex-col gap-3.5">
        {etfs.map(([sym, etf]) => {
          const sw = etf.sector_weightings || {};
          const total = Object.values(sw).reduce((a, b) => a + b, 0);
          return (
            <div key={sym} className="grid grid-cols-[100px_1fr] items-center gap-3">
              <div>
                <div className="font-mono text-sm font-bold">{sym}</div>
                <div className="text-[10px] text-zinc-500 truncate">{etf.name}</div>
              </div>
              <div className="flex h-7 w-full border border-zinc-900">
                {SECTOR_ORDER.map((sec) => {
                  const v = sw[sec] || 0;
                  if (v === 0) return null;
                  const w = total > 0 ? (v / total) * 100 : 0;
                  return (
                    <div
                      key={sec}
                      title={`${SECTOR_LABELS[sec] || sec}: ${(v * 100).toFixed(1)}%`}
                      style={{ width: `${w}%`, background: SECTOR_COLOR[sec] || "#444" }}
                      className="h-full"
                    />
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-x-3 gap-y-1.5 mt-4 pt-3 border-t border-zinc-900">
        {SECTOR_ORDER.map((sec) => (
          <span
            key={sec}
            className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider text-zinc-400"
          >
            <span
              className="inline-block w-2.5 h-2.5"
              style={{ background: SECTOR_COLOR[sec] }}
            />
            {SECTOR_LABELS[sec]}
          </span>
        ))}
      </div>
    </div>
  );
}
